import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import useRequest from "../../hooks/useRequest";
import Breadcrumb from "../Breadcrumb/Breadcrumb";

const PREFIX = "OrderReturn";
const KEYS = {
  termsEn: `${PREFIX}.terms-en`,
  termsAr: `${PREFIX}.terms-ar`,
  reasonsEn: `${PREFIX}.reasons-en`,
  reasonsAr: `${PREFIX}.reasons-ar`,
  windowDays: `${PREFIX}.window-days`,
  requireDelivery: `${PREFIX}.require-delivery`,
  requireQualityCheck: `${PREFIX}.require-quality-check`,
  defaultReturnable: `${PREFIX}.default-returnable`,
};

const parseLines = (value = "") =>
  String(value)
    .split(/\n/)
    .map((item) => item.trim())
    .filter(Boolean);

const sectionStyle = {
  background: "#fff",
  border: "1px solid #E4E6EF",
  borderRadius: "8px",
  padding: "20px 24px",
  marginBottom: "20px",
};

const sectionTitleStyle = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#181C32",
  marginBottom: "4px",
};

const sectionHelpStyle = {
  fontSize: "13px",
  color: "#7E8299",
  marginBottom: "16px",
};

const labelStyle = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#3F4254",
  marginBottom: "6px",
  display: "block",
};

const chipActive = {
  border: "1px solid #3699FF",
  background: "#EEF6FF",
  color: "#3699FF",
};

const chipIdle = {
  border: "1px solid #E4E6EF",
  background: "#F3F6F9",
  color: "#3F4254",
};

const ReasonListEditor = ({
  title,
  help,
  reasons,
  setReasons,
  placeholder,
  dir = "ltr",
}) => {
  const [draft, setDraft] = useState("");

  const addReason = () => {
    const next = draft.trim();
    if (!next) return;
    if (reasons.includes(next)) {
      toast.error("This reason already exists.");
      return;
    }
    setReasons([...reasons, next]);
    setDraft("");
  };

  return (
    <div className="col-lg-6 mb-5">
      <div style={sectionStyle} className="h-100">
        <div style={sectionTitleStyle}>{title}</div>
        <div style={sectionHelpStyle}>{help}</div>
        <div className="d-flex align-items-center mb-4">
          <input
            type="text"
            className="form-control"
            style={{ background: "#F3F6F9", borderColor: "#E4E6EF" }}
            placeholder={placeholder}
            value={draft}
            dir={dir}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addReason();
              }
            }}
          />
          <button
            type="button"
            className="btn btn-primary font-weight-bold ml-3"
            style={{ minWidth: "90px" }}
            onClick={addReason}
          >
            Add
          </button>
        </div>
        {reasons.length === 0 ? (
          <div
            className="text-center py-5"
            style={{
              background: "#F9F9FC",
              border: "1px dashed #E4E6EF",
              borderRadius: "6px",
              color: "#B5B5C3",
              fontSize: "13px",
            }}
          >
            No reasons added yet
          </div>
        ) : (
          reasons.map((reason, index) => (
            <div
              key={`${reason}-${index}`}
              className="d-flex align-items-center justify-content-between mb-2 px-3 py-3"
              dir={dir}
              style={{
                background: "#F9F9FC",
                border: "1px solid #EEF0F8",
                borderRadius: "6px",
              }}
            >
              <span style={{ fontSize: "14px", color: "#181C32" }}>
                {index + 1}. {reason}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-light-danger"
                onClick={() =>
                  setReasons(reasons.filter((_, i) => i !== index))
                }
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const OrderReturn = () => {
  const { register, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      termsEn: "",
      termsAr: "",
      windowDays: "7",
      requireDelivery: "true",
      requireQualityCheck: "true",
      defaultReturnable: "true",
    },
  });

  const [settingsMeta, setSettingsMeta] = useState({});
  const [reasonsEn, setReasonsEn] = useState([]);
  const [reasonsAr, setReasonsAr] = useState([]);

  const { response, request } = useRequest();
  const { response: responseFetchSetting, request: requestFetchSetting } =
    useRequest();

  useEffect(() => {
    document.title = "Order Return Setting - Easyspare";
    requestFetchSetting("GET", `setting/all-prefix/${PREFIX}`);
  }, []);

  useEffect(() => {
    if (!responseFetchSetting) return;
    const { prefixSettings = [] } = responseFetchSetting;
    const meta = {};

    prefixSettings.forEach((setting) => {
      meta[setting.key] = setting;
      const value = setting.selected || setting.value || "";
      if (setting.key === KEYS.termsEn) setValue("termsEn", value);
      if (setting.key === KEYS.termsAr) setValue("termsAr", value);
      if (setting.key === KEYS.windowDays) setValue("windowDays", value || "7");
      if (setting.key === KEYS.requireDelivery)
        setValue("requireDelivery", value || "true");
      if (setting.key === KEYS.requireQualityCheck)
        setValue("requireQualityCheck", value || "true");
      if (setting.key === KEYS.defaultReturnable)
        setValue("defaultReturnable", value || "true");
      if (setting.key === KEYS.reasonsEn) setReasonsEn(parseLines(value));
      if (setting.key === KEYS.reasonsAr) setReasonsAr(parseLines(value));
    });
    setSettingsMeta(meta);
  }, [responseFetchSetting, setValue]);

  useEffect(() => {
    if (response) toast.success("Order return settings updated successfully.");
  }, [response]);

  const onSubmit = (data) => {
    if (!reasonsEn.length) {
      toast.error("Please add at least one English return reason.");
      return;
    }

    const dataToSubmit = [
      { key: KEYS.termsEn, value: data.termsEn || "" },
      { key: KEYS.termsAr, value: data.termsAr || "" },
      { key: KEYS.reasonsEn, value: reasonsEn.join("\n") },
      { key: KEYS.reasonsAr, value: reasonsAr.join("\n") },
      { key: KEYS.windowDays, value: String(data.windowDays || "7") },
    ];

    const otherData = [
      { key: KEYS.requireDelivery, selected: data.requireDelivery || "true" },
      {
        key: KEYS.requireQualityCheck,
        selected: data.requireQualityCheck || "true",
      },
      {
        key: KEYS.defaultReturnable,
        selected: data.defaultReturnable || "true",
      },
    ];

    const formData = new FormData();
    formData.append("imagesKey", JSON.stringify([]));
    formData.append(
      "data",
      JSON.stringify(dataToSubmit.filter((item) => settingsMeta[item.key]))
    );
    formData.append(
      "otherData",
      JSON.stringify(otherData.filter((item) => settingsMeta[item.key]))
    );
    request("PUT", "setting/all-prefix", formData);
  };

  const requireDelivery = watch("requireDelivery");
  const requireQualityCheck = watch("requireQualityCheck");
  const defaultReturnable = watch("defaultReturnable");

  const YesNo = ({ value, current, onSelect, yesLabel, noLabel }) => (
    <div className="d-flex flex-wrap">
      {[
        { value: "true", label: yesLabel },
        { value: "false", label: noLabel },
      ].map((option) => {
        const active = current === option.value;
        return (
          <button
            key={option.value}
            type="button"
            className="btn mr-3 mb-2"
            style={{
              ...(active ? chipActive : chipIdle),
              borderRadius: "6px",
              minWidth: "180px",
              fontWeight: 700,
              fontSize: "13px",
              padding: "10px 16px",
            }}
            onClick={() => onSelect(option.value)}
          >
            {option.label}
          </button>
        );
      })}
      <input type="hidden" {...register(value)} />
    </div>
  );

  return (
    <div className="content d-flex flex-column flex-column-fluid">
      <Breadcrumb
        title="Order Return Setting"
        links={[
          { to: "/", name: "Dashboard" },
          { name: "Order Return Setting" },
        ]}
      />

      <div className="d-flex flex-column-fluid">
        <div className="container">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>1. Return Policy Text</div>
              <div style={sectionHelpStyle}>
                Customer app mein return se pehle ye terms dikhengi.
              </div>
              <div className="row">
                <div className="col-lg-6 mb-4 mb-lg-0">
                  <label style={labelStyle}>English</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    style={{ background: "#F3F6F9", borderColor: "#E4E6EF" }}
                    {...register("termsEn")}
                  />
                </div>
                <div className="col-lg-6">
                  <label style={labelStyle}>Arabic</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    dir="rtl"
                    style={{ background: "#F3F6F9", borderColor: "#E4E6EF" }}
                    {...register("termsAr")}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <ReasonListEditor
                title="2. Return Reasons (English)"
                help="Customer specific item return karte time ye options select karega."
                reasons={reasonsEn}
                setReasons={setReasonsEn}
                placeholder="Type reason and click Add"
              />
              <ReasonListEditor
                title="2. Return Reasons (Arabic)"
                help="Same reasons Arabic mein."
                reasons={reasonsAr}
                setReasons={setReasonsAr}
                placeholder="السبب ثم Add"
                dir="rtl"
              />
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>3. Return Rules</div>
              <div style={sectionHelpStyle}>
                Delivery ke baad kitne din tak return, quality check, aur default
                returnable policy.
              </div>

              <div className="row mb-6">
                <div className="col-md-4 mb-4">
                  <label style={labelStyle}>
                    Return window (days from delivery)
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    style={{ background: "#F3F6F9", borderColor: "#E4E6EF" }}
                    {...register("windowDays")}
                  />
                </div>
              </div>

              <label style={labelStyle}>Only after delivery?</label>
              <YesNo
                value="requireDelivery"
                current={requireDelivery}
                onSelect={(v) => setValue("requireDelivery", v)}
                yesLabel="Yes, after delivery only"
                noLabel="No, allow earlier"
              />

              <label style={{ ...labelStyle, marginTop: "16px" }}>
                Admin quality check required?
              </label>
              <YesNo
                value="requireQualityCheck"
                current={requireQualityCheck}
                onSelect={(v) => setValue("requireQualityCheck", v)}
                yesLabel="Yes, QC then approve"
                noLabel="No instant approve path"
              />

              <label style={{ ...labelStyle, marginTop: "16px" }}>
                Products returnable by default?
              </label>
              <div style={sectionHelpStyle}>
                Agar No: har product pe `isReturnable=false` hoga unless offer
                pe true set ho. Default Yes rakho.
              </div>
              <YesNo
                value="defaultReturnable"
                current={defaultReturnable}
                onSelect={(v) => setValue("defaultReturnable", v)}
                yesLabel="Yes, returnable"
                noLabel="No, not returnable"
              />
            </div>

            <div className="d-flex justify-content-end pb-10">
              <button
                type="submit"
                className="btn btn-primary font-weight-bolder px-10 py-4"
              >
                Save Settings
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderReturn;
